'use client'

import { useState, useEffect, useRef, useCallback } from 'react'
import { AlertTriangle, RefreshCw, WifiOff } from 'lucide-react'
import { fetchTechDirectorDashboard, type DashboardData } from '@/lib/tech-director-dashboard-service'
import { ProaResultGlobal } from '../ProaResultGlobal'
// TODO: Re-enable when 2025 data is available
// import { PriorityActionsWidget } from './PriorityActionsWidget'
// import { KeyMetricsCards } from './KeyMetricsCards'
// import { CategoryNavigationCards } from './CategoryNavigationCards'

const AUTO_RETRY_DELAYS = [3000, 5000, 10000] // Retry after 3s, 5s, 10s
const MAX_AUTO_RETRIES = 3

export function TechDirectorDashboardNew() {
  const [dashboardData, setDashboardData] = useState<DashboardData | null>(null)
  const [isLoading, setIsLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [retryCount, setRetryCount] = useState(0)
  const [isRetrying, setIsRetrying] = useState(false)
  const retryTimeoutRef = useRef<NodeJS.Timeout | null>(null)

  const loadDashboard = useCallback(async (isAutoRetry = false) => {
    try {
      if (!isAutoRetry) {
        setIsLoading(true)
        setRetryCount(0)
      }
      setIsRetrying(isAutoRetry)
      setError(null)
      const data = await fetchTechDirectorDashboard()
      setDashboardData(data)
      setRetryCount(0) // Reset retry count on success
    } catch (err) {
      console.error('Error loading dashboard:', err)
      const errorMessage = err instanceof Error ? err.message : 'Failed to load dashboard data'
      setError(errorMessage)

      // Auto-retry for network errors (only if we don't have data yet)
      if (!dashboardData && retryCount < MAX_AUTO_RETRIES) {
        const delay = AUTO_RETRY_DELAYS[retryCount] || AUTO_RETRY_DELAYS[AUTO_RETRY_DELAYS.length - 1]
        console.log(`[Dashboard] Auto-retry ${retryCount + 1}/${MAX_AUTO_RETRIES} in ${delay}ms`)
        retryTimeoutRef.current = setTimeout(() => {
          setRetryCount(prev => prev + 1)
          loadDashboard(true)
        }, delay)
      }
    } finally {
      setIsLoading(false)
      setIsRetrying(false)
    }
  }, [dashboardData, retryCount])

  useEffect(() => {
    loadDashboard()

    // Cleanup retry timeout on unmount
    return () => {
      if (retryTimeoutRef.current) {
        clearTimeout(retryTimeoutRef.current)
      }
    }
  }, []) // eslint-disable-line react-hooks/exhaustive-deps

  const handleManualRetry = () => {
    if (retryTimeoutRef.current) {
      clearTimeout(retryTimeoutRef.current)
    }
    setRetryCount(0)
    loadDashboard(false)
  }

  // Loading State
  if (isLoading) {
    return (
      <div className="space-y-6">
        {/* Header Skeleton */}
        <div className="animate-pulse">
          <div className="h-8 w-64 bg-brand-dark-light rounded mb-2 relative overflow-hidden">
            <div className="absolute inset-0 bg-gradient-to-r from-transparent via-brand-gray/10 to-transparent animate-shimmer" style={{ backgroundSize: '200% 100%' }} />
          </div>
          <div className="h-4 w-96 bg-brand-dark-light rounded relative overflow-hidden">
            <div className="absolute inset-0 bg-gradient-to-r from-transparent via-brand-gray/10 to-transparent animate-shimmer" style={{ backgroundSize: '200% 100%' }} />
          </div>
        </div>

        {/* PROA x Resultado Skeleton */}
        <div className="card animate-pulse">
          <div className="h-64 bg-brand-dark-light rounded relative overflow-hidden">
            <div className="absolute inset-0 bg-gradient-to-r from-transparent via-brand-gray/10 to-transparent animate-shimmer" style={{ backgroundSize: '200% 100%' }} />
          </div>
        </div>
      </div>
    )
  }

  // Error State (only show if we have no data at all)
  if (error && !dashboardData) {
    const isAutoRetryActive = retryCount < MAX_AUTO_RETRIES && retryCount > 0
    const isNetworkError = error.toLowerCase().includes('network')

    return (
      <div className="card">
        <div className="text-center py-12">
          <div className="w-16 h-16 bg-error/20 rounded-full flex items-center justify-center mx-auto mb-4">
            {isNetworkError ? (
              <WifiOff className="text-error" size={32} />
            ) : (
              <AlertTriangle className="text-error" size={32} />
            )}
          </div>
          <h2 className="font-display text-xl font-bold text-white mb-2">
            {isNetworkError ? 'Sem Conexão' : 'Falha ao Carregar Dashboard'}
          </h2>
          <p className="text-brand-gray mb-4 max-w-md mx-auto">{error}</p>

          {isAutoRetryActive || isRetrying ? (
            <div className="flex items-center justify-center gap-2 text-brand-gray-light mb-6">
              <RefreshCw className="animate-spin" size={16} />
              <span>Tentando novamente... ({retryCount}/{MAX_AUTO_RETRIES})</span>
            </div>
          ) : retryCount >= MAX_AUTO_RETRIES ? (
            <p className="text-brand-gray-light mb-6 text-sm">
              Tentativas automáticas esgotadas. Verifique sua conexão.
            </p>
          ) : null}

          <button
            onClick={handleManualRetry}
            disabled={isRetrying}
            className="btn-primary disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {isRetrying ? (
              <span className="flex items-center gap-2">
                <RefreshCw className="animate-spin" size={16} />
                Carregando...
              </span>
            ) : (
              'Tentar Novamente'
            )}
          </button>
        </div>
      </div>
    )
  }

  // No Data State
  if (!dashboardData) {
    return (
      <div className="card">
        <div className="text-center py-12">
          <p className="text-brand-gray">Nenhum dado disponível no dashboard</p>
        </div>
      </div>
    )
  }

  // Map of source names to friendly Portuguese labels
  const sourceLabels: Record<string, string> = {
    employees: 'colaboradores',
    clients: 'clientes',
    vacation: 'férias',
    performance: 'desempenho',
    calendar: 'calendário',
    budget: 'orçamento'
  }

  return (
    <div className="space-y-6">
      {/* Partial Data Warning Banner */}
      {dashboardData?.failedSources && dashboardData.failedSources.length > 0 && (
        <div className="bg-warning/10 border border-warning/30 rounded-lg p-4 animate-fade-in-up">
          <div className="flex items-start gap-3">
            <AlertTriangle className="text-warning flex-shrink-0 mt-0.5" size={20} />
            <div className="flex-1 min-w-0">
              <p className="text-warning font-medium text-sm">
                Alguns dados podem estar desatualizados
              </p>
              <p className="text-brand-gray text-sm mt-1">
                Não foi possível carregar: {dashboardData.failedSources.map(s => sourceLabels[s] || s).join(', ')}.
                {dashboardData.fromCache && ' Exibindo dados em cache.'}
              </p>
            </div>
            <button
              onClick={handleManualRetry}
              disabled={isRetrying}
              className="flex items-center gap-1.5 text-sm text-warning hover:text-warning/80 transition-colors disabled:opacity-50"
            >
              <RefreshCw className={isRetrying ? 'animate-spin' : ''} size={14} />
              {isRetrying ? 'Atualizando...' : 'Atualizar'}
            </button>
          </div>
        </div>
      )}

      {/* Header */}
      <div className="animate-fade-in-up">
        <h1 className="font-display text-3xl font-bold text-white mb-2">
          Dashboard do Diretor Técnico
        </h1>
        <p className="text-brand-gray-light">
          Seu centro de comando para equipe, desempenho e métricas de negócio
        </p>
      </div>

      {/* PROA x Resultado Global Component */}
      <div className="animate-stagger-1">
        <ProaResultGlobal />
      </div>

      {/* TODO: Re-enable when 2025 data is available

      {/* Priority Actions (Top 40%) */}
      {/* <div className="animate-stagger-2">
        <PriorityActionsWidget actions={dashboardData.priorityActions} />
      </div> */}

      {/* Key Metrics Cards (Middle) */}
      {/* <div className="animate-stagger-3">
        <KeyMetricsCards metrics={dashboardData.keyMetrics} clients={dashboardData.rawData.clients} />
      </div> */}

      {/* Category Navigation (Bottom 60%) */}
      {/* <div className="animate-stagger-4">
        <h2 className="font-display text-2xl font-semibold text-white mb-4">
          Acesso Rápido
        </h2>
        <CategoryNavigationCards categories={dashboardData.categoryCards} />
      </div> */}

      {/* Data Source Info */}
      {/* <div className="text-center">
        <p className="text-xs text-brand-gray">
          <span className="inline-flex items-center gap-1">
            <span className="w-2 h-2 rounded-full bg-success animate-pulse"></span>
            100% Dados Reais: Colaboradores ({dashboardData.rawData.employees.length}), Clientes ({dashboardData.rawData.clients.length}), Férias, Desempenho, Orçamento & Calendário
          </span>
        </p>
      </div> */}

      {/* Coming Soon placeholder */}
      <div className="animate-stagger-2">
        <div className="card border-dashed border-brand-gray/30">
          <div className="text-center py-8">
            <p className="text-brand-gray text-sm">
              Mais métricas e ações em breve...
            </p>
          </div>
        </div>
      </div>
    </div>
  )
}
